/**
 * OpenDisputeModal - Modal dialog for initiating a formal dispute
 *
 * DevStory 4.5:
 * Criterio 2 - Disputa desde el primer rechazo (disponible para cliente y coordinador)
 * Criterio 4 - Apertura por cliente con argumentación obligatoria
 * Criterio 5 - Apertura por coordinador con argumentación obligatoria
 * Criterio 6 - Registro automático del contexto e histórico
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/ui';
import { useCreateDispute } from '@hooks/useDisputes';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { Milestone, Project } from '@/types';
import { pendingProjectCommand } from '@/services/projectCommands';
import { useAuthStore } from '@/stores/authStore';

interface OpenDisputeModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Pick<Project, 'id' | 'title'>;
  milestone: Pick<Milestone, 'id' | 'title' | 'description' | 'budget'>;
  userRole: 'client' | 'consultant';
}

export function OpenDisputeModal({
  isOpen,
  onClose,
  project,
  milestone,
  userRole,
}: OpenDisputeModalProps): JSX.Element | null {
  const navigate = useNavigate();
  const { currency } = useDaoConfig();
  const [reason, setReason] = useState(() => {
    const principal = useAuthStore.getState().session?.principalId;
    try {
      const saved = principal
        ? pendingProjectCommand(principal, project.id)
        : null;
      return saved?.command.kind === 'dispute-open' &&
        saved.command.payload.milestoneId === milestone.id
        ? saved.command.payload.reason
        : '';
    } catch {
      return '';
    }
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const createDisputeMutation = useCreateDispute();
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector('textarea')?.focus();
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !createDisputeMutation.isPending) onClose();
      if (event.key !== 'Tab') return;
      const controls = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled])'
      );
      const first = controls?.[0],
        last = controls?.[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => {
      document.removeEventListener('keydown', keyboard);
      previous?.focus();
    };
  }, [isOpen, onClose, createDisputeMutation.isPending]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = reason.trim();
      if (!trimmed) {
        setErrorMsg(
          'Please provide a mandatory argument/reason for opening the dispute.'
        );
        return;
      }
      if (!milestone.id) {
        setErrorMsg('Milestone ID is missing.');
        return;
      }

      setErrorMsg(null);
      createDisputeMutation.mutate(
        {
          projectId: project.id,
          milestoneId: milestone.id,
          reason: trimmed,
        },
        {
          onSuccess: (dispute) => {
            onClose();
            navigate(
              `/projects/${project.id}/milestones/${milestone.id}/disputes/${dispute.id}`
            );
          },
          onError: (err) => {
            setErrorMsg(
              err.message || 'Failed to open dispute. Please try again.'
            );
          },
        }
      );
    },
    [reason, milestone.id, project.id, createDisputeMutation, onClose, navigate]
  );

  if (!isOpen) return null;

  const budgetDisplay =
    milestone.budget !== undefined && milestone.budget !== null
      ? `${milestone.budget} ${currency.symbol}`
      : 'Budget Pending';

  return (
    <div className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        className="w-full max-w-xl space-y-5 rounded-2xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6 shadow-2xl"
        role="dialog"
        ref={dialogRef}
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400">
              <i className="ri-scales-3-line text-xl" />
            </div>
            <div>
              <h3
                id="modal-title"
                className="text-lg font-bold text-[var(--text-dark-primary,#f5f5f5)]"
              >
                Open Formal Dispute
              </h3>
              <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                Initiated by{' '}
                {userRole === 'client' ? 'Client' : 'Project Coordinator'} ·
                Formal dispute
              </p>
            </div>
          </div>
          <button
            aria-label="Close dispute modal"
            onClick={onClose}
            disabled={createDisputeMutation.isPending}
            className="text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] transition-colors hover:text-white"
          >
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        {/* Milestone Context Box */}
        <div className="space-y-2 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-4">
          <div className="flex items-center justify-between text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            <span>
              Project: <strong className="text-white">{project.title}</strong>
            </span>
            <span className="font-semibold text-[var(--state-brand-active,#36d399)]">
              {budgetDisplay}
            </span>
          </div>
          <p className="text-sm font-semibold text-white">{milestone.title}</p>
          {milestone.description && (
            <p className="line-clamp-2 text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
              {milestone.description}
            </p>
          )}
          <div className="flex items-center gap-2 border-t border-[var(--base-border,#3d3d3d)] pt-2 text-xs text-rose-400">
            <i className="ri-error-warning-line" />
            <span>
              Milestone is in rejected status. Opening a dispute freezes the
              project and unpaid escrow.
            </span>
          </div>
        </div>

        {/* Process Explanation */}
        <div className="space-y-1.5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3.5 text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
          <p className="flex items-center gap-1.5 font-semibold text-amber-300">
            <i className="ri-information-line" /> What happens when you open a
            dispute?
          </p>
          <ul className="list-inside list-disc space-y-1 pl-1 text-[11px] leading-relaxed">
            <li>
              An immutable public case record is compiled with the milestone
              history and deliverables.
            </li>
            <li>
              The counterparty is notified and given rights to submit their
              counter-arguments.
            </li>
            <li>
              A dedicated direct communication channel is automatically
              provisioned for both parties.
            </li>
            <li>
              The whole project and unpaid escrow are frozen. Resolution and
              unlock are not available yet.
            </li>
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="opening-reason"
              className="mb-1.5 block text-xs font-semibold text-[var(--text-dark-primary,#f5f5f5)]"
            >
              Opening Arguments & Justification{' '}
              <span className="text-rose-400">*</span>
            </label>
            <textarea
              id="opening-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errorMsg) setErrorMsg(null);
              }}
              placeholder="Explain clearly why this milestone rejection is disputed or justified. Detail all requirements, criteria, or unfulfilled aspects..."
              rows={4}
              required
              className="w-full resize-none rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-3 text-sm text-[var(--text-dark-primary,#f5f5f5)] placeholder:text-[var(--text-dark-secondary,rgba(255,255,255,0.4))] focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
          </div>

          {errorMsg && (
            <p
              role="alert"
              className="flex items-center gap-1 text-xs text-rose-400"
            >
              <i className="ri-alert-line" /> {errorMsg}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={createDisputeMutation.isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              isLoading={createDisputeMutation.isPending}
              disabled={createDisputeMutation.isPending || !reason.trim()}
              className="flex-1 !border-amber-600 !bg-amber-600 font-semibold text-white hover:!bg-amber-700"
            >
              <i className="ri-scales-3-line mr-1.5" />
              Confirm & Open Dispute
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
